"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { HttpTypes } from "@medusajs/types"
import { loadMoreProducts, searchProductsMeili } from "@lib/data/products"
import { getRegion } from "@lib/data/regions"
import ProductPreview from "@modules/products/components/product-preview"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

const PRODUCT_LIMIT = 50

type InfiniteProductsProps = {
  initialProducts: HttpTypes.StoreProduct[] | null
  initialCount: number
  initialPage?: number
  countryCode: string
  collectionId?: string
  categoryId?: string
  q?: string
  minPrice?: string
  maxPrice?: string
  tags?: string
  brand?: string
  sortBy?: SortOptions
  // T9: región resuelta server-side (PaginatedProducts) para que el grid
  // se renderice en el HTML inicial; si llega null se obtiene client-side
  // como fallback (comportamiento anterior).
  region?: HttpTypes.StoreRegion | null
}

export default function InfiniteProducts({
  initialProducts,
  initialCount,
  initialPage,
  countryCode,
  collectionId,
  categoryId,
  q,
  minPrice,
  maxPrice,
  tags,
  brand,
  sortBy = "stock",
  region: initialRegion,
}: InfiniteProductsProps) {
  const [products, setProducts] = useState<HttpTypes.StoreProduct[]>(initialProducts || [])
  const [page, setPage] = useState((initialPage ?? 1) + 1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [region, setRegion] = useState<HttpTypes.StoreRegion | null>(initialRegion || null)
  const loaderRef = useRef<HTMLDivElement>(null)

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({
    collectionId,
    categoryId,
    q,
    minPrice,
    maxPrice,
    tags,
    brand,
    sortBy
  })

  const tagsArray = tags ? tags.split(",").filter(Boolean) : []
  const brandsArray = brand ? brand.split(",").filter(Boolean) : []
  const collectionsArray = collectionId ? collectionId.split(",").filter(Boolean) : []
  const categoriesArray = categoryId ? categoryId.split(",").filter(Boolean) : []
  const minPriceNum = minPrice ? parseFloat(minPrice) : undefined
  const maxPriceNum = maxPrice ? parseFloat(maxPrice) : undefined

  // Fetch products with current filters
  const fetchProducts = useCallback(async (pageNum: number) => {
    const result = await searchProductsMeili({
      q: q || "",
      collectionIds: collectionsArray,
      categoryIds: categoriesArray,
      tags: tagsArray,
      brands: brandsArray,
      minPrice: minPriceNum,
      maxPrice: maxPriceNum,
      sortBy: sortBy,
      page: pageNum,
      limit: PRODUCT_LIMIT,
    })
    return result
  }, [q, collectionsArray, categoriesArray, tagsArray, brandsArray, minPriceNum, maxPriceNum, sortBy])

  // Load more products for infinite scroll
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return

    setLoading(true)
    try {
      const result = await fetchProducts(page)

      if (result.products.length > 0) {
        setProducts((prev) => [...prev, ...result.products])
        setPage((prev) => prev + 1)
        setHasMore(result.products.length >= PRODUCT_LIMIT)
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error("Error loading more products:", error)
    } finally {
      setLoading(false)
    }
  }, [page, loading, hasMore, fetchProducts])

  // Detect filter changes and reload products
  useEffect(() => {
    const prevFilters = prevFiltersRef.current
    const filtersChanged =
      prevFilters.collectionId !== collectionId ||
      prevFilters.categoryId !== categoryId ||
      prevFilters.q !== q ||
      prevFilters.minPrice !== minPrice ||
      prevFilters.maxPrice !== maxPrice ||
      prevFilters.tags !== tags ||
      prevFilters.brand !== brand ||
      prevFilters.sortBy !== sortBy

    if (filtersChanged) {
      // Filters changed, reload products from page 1
      setLoading(true)
      setPage((initialPage ?? 1) + 1)

      fetchProducts(1).then((result) => {
        setProducts(result.products)
        setHasMore(result.products.length >= PRODUCT_LIMIT)
        setLoading(false)
      }).catch((error) => {
        console.error("Error reloading products:", error)
        setLoading(false)
      })

      // Update previous filters
      prevFiltersRef.current = {
        collectionId,
        categoryId,
        q,
        minPrice,
        maxPrice,
        tags,
        brand,
        sortBy
      }
    }
  }, [collectionId, categoryId, q, minPrice, maxPrice, tags, brand, sortBy, fetchProducts])

  // Initialize hasMore based on initial products
  useEffect(() => {
    const prods = initialProducts || []
    setHasMore(prods.length > 0 && initialCount > prods.length)
  }, [initialProducts, initialCount])

  // T9: si la región ya viene del server (SSR), no hace falta pedirla.
  // Fallback client-side solo si PaginatedProducts no pudo resolverla.
  useEffect(() => {
    if (initialRegion) return
    getRegion(countryCode).then((r) => {
      if (r) setRegion(r)
    })
  }, [countryCode, initialRegion])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => observer.disconnect()
  }, [hasMore, loading, loadMore])

  if (!region) {
    return null
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-8 text-ui-fg-muted">
        <p>No hay productos</p>
      </div>
    )
  }

  return (
    <>
      <ul
        className="grid grid-cols-2 w-full small:grid-cols-3 medium:grid-cols-4 gap-x-6 gap-y-8"
        data-testid="products-list"
      >
        {products.map((p) => (
          <li key={p.id}>
            <ProductPreview product={p} region={region} />
          </li>
        ))}
      </ul>

      {/* Infinite scroll loader - always visible for IntersectionObserver */}
      <div
        ref={loaderRef}
        className="flex justify-center items-center py-8 w-full min-h-[50px]"
      >
        {loading && (
          <div className="flex items-center gap-2 text-ui-fg-muted">
            <div className="w-6 h-6 border-2 border-t-transparent border-[#f57aa9] rounded-full animate-spin" />
            <span>Cargando más productos...</span>
          </div>
        )}
        {!loading && !hasMore && products.length > 0 && (
          <p className="text-ui-fg-muted">No hay más productos</p>
        )}
      </div>
    </>
  )
}
